import { css, html, nothing } from 'lit';
import { property } from 'lit/decorators.js';
import { positionsTableEndpoints } from '../generated/endpoint-defaults.js';
import { planetGlyph, signGlyph } from '../tokens/index.js';
import type {
	ArabicLotsResponse,
	AsteroidsResponse,
	LilithResponse,
	ProgressionsResponse,
	SolarArcResponse,
} from '../types/index.js';
import { RoxyDataElement } from '../utils/base-element.js';
import { baseStyles } from '../utils/base-styles.js';
import { customElement } from '../utils/define.js';
import {
	formatDegreeInSign,
	longitudeToSignPosition,
} from '../utils/degree.js';
import { disclosureStyles } from '../utils/disclosure.js';
import { formatDate, formatNumber } from '../utils/format.js';
import {
	type InterpSection,
	interpAccordionStyles,
} from '../utils/interp-accordion.js';
import { display } from '../utils/localized.js';
import { capitalize } from '../utils/string.js';

/**
 * Union of the position-list Western responses this one editorial table renders.
 * Each carries an array of bodies in zodiac signs plus a per-body
 * interpretation; the table discriminates on which array key is present.
 */
type PositionsResponse =
	| AsteroidsResponse
	| LilithResponse
	| ProgressionsResponse
	| SolarArcResponse
	| ArabicLotsResponse;

interface Row {
	/** The canonical body name a glyph is looked up by, where the printed label is its translation. */
	body?: string;
	label: string;
	sign: string;
	/** The sign as printed; the canonical `sign` keys the glyph. */
	signLabel?: string;
	degree: number;
	house?: number;
	speed?: number;
	isRetrograde?: boolean;
	formula?: string;
	natalLongitude?: number;
	interpretation?: string;
	isAngle?: boolean;
}

interface ViewModel {
	title: string;
	badges: Array<{ label: string; value: string }>;
	summary?: string;
	rows: Row[];
	cols: { house: boolean; motion: boolean; formula: boolean; natal: boolean };
}

/**
 * Editorial positions table for the Western point-list endpoints: asteroids,
 * Black Moon Lilith, secondary progressions, solar arc directions, and the
 * Arabic lots. One component, five shapes: it detects the response by its array
 * key and shows only the columns that response carries (house, motion, formula,
 * or a natal-to-directed comparison), then lists each body reading below.
 */
@customElement('roxy-positions-table')
export class RoxyPositionsTable extends RoxyDataElement<PositionsResponse> {
	static endpoints = positionsTableEndpoints;

	static styles = [
		baseStyles,
		disclosureStyles,
		interpAccordionStyles,
		css`.wrap{background:var(--roxy-surface,#fff);width:100%;color:var(--roxy-fg,#0a0a0a);border:1px solid var(--roxy-border,#e4e4e7);border-radius:var(--roxy-radius-md,8px);padding:var(--roxy-space-lg,1.5rem);box-shadow:var(--roxy-shadow-sm);gap:var(--roxy-space-md,1rem);grid-template-columns:minmax(0,1fr);display:grid}header{align-items:baseline;gap:var(--roxy-space-sm,.5rem) var(--roxy-space-md,1rem);flex-wrap:wrap;display:flex}.title{font-size:var(--roxy-text-lg,1.125rem);font-weight:var(--roxy-weight-bold,600);color:var(--roxy-primary,#0f172a);margin:0}.badges{gap:var(--roxy-space-xs,.25rem);flex-wrap:wrap;display:flex}.badge{border-radius:var(--roxy-radius-full,9999px);font-size:var(--roxy-text-xs,.75rem);background:color-mix(in srgb, var(--roxy-accent,#f59e0b) 14%, transparent);color:var(--roxy-fg,#0a0a0a);padding:2px 8px}.badge b{color:var(--roxy-accent-ink,#b45309);font-weight:600}.summary{color:var(--roxy-fg,#0a0a0a);font-size:var(--roxy-text-sm,.875rem);margin:0}.scroll{-webkit-overflow-scrolling:touch;min-width:0;overflow-x:auto}table{border-collapse:collapse;width:100%;font-size:var(--roxy-text-sm,.875rem)}caption{text-align:left;color:var(--roxy-muted,#71717a);font-size:var(--roxy-text-xs,.75rem);padding-bottom:var(--roxy-space-xs,.25rem)}th,td{text-align:left;border-bottom:1px solid var(--roxy-border,#e4e4e7);white-space:nowrap;padding:6px 10px}th{color:var(--roxy-muted,#71717a);text-transform:uppercase;letter-spacing:.04em;font-weight:600;font-size:var(--roxy-text-xs,.75rem)}td.num{text-align:right;font-variant-numeric:tabular-nums}.body-cell{color:var(--roxy-fg,#0a0a0a);font-weight:500}.body-cell .glyph{color:var(--roxy-accent-ink,#b45309);margin-right:.35rem}tr.angle td{color:var(--roxy-secondary,#475569)}.sign{align-items:baseline;gap:.3rem;display:inline-flex}.sign .sg{color:var(--roxy-secondary,#475569)}.retro{color:var(--roxy-danger,#dc2626);margin-left:.3em;font-weight:600}.formula{color:var(--roxy-muted,#71717a);font-variant-numeric:tabular-nums;white-space:normal}`,
	];

	/** Override the auto-derived heading. Empty keeps the per-shape default (e.g. "Asteroids"). */
	@property({ type: String })
	heading = '';

	protected renderData(data: PositionsResponse) {
		const vm = this.toViewModel(data);
		const cols = vm.cols;
		return html`<div class="wrap" part="card"><header part="header"><h2 class="title">${this.heading || vm.title}</h2>${
					vm.badges.length
						? html`<div class="badges" part="details">${vm.badges.map((b) => html`<span class="badge"><b>${b.label}</b> ${b.value}</span>`)}</div>`
						: nothing
				}</header>${
				// The response `summary` is a written overview of the positions, not a
				// count of them, so it goes with the readings.
				vm.summary && !this.hideReadings
					? html`<p class="summary">${vm.summary}</p>`
					: nothing
			}<div class="scroll" part="table"><table><caption>${vm.title}</caption><thead><tr><th scope="col">${this.t('Body')}</th><th scope="col">${this.t('Position')}</th>${cols.natal ? html`<th scope="col">${this.t('Natal')}</th>` : nothing} ${cols.house ? html`<th scope="col" class="num">${this.t('House')}</th>` : nothing} ${cols.motion ? html`<th scope="col">${this.t('Motion')}</th>` : nothing} ${cols.formula ? html`<th scope="col">${this.t('Formula')}</th>` : nothing}</tr></thead><tbody>${vm.rows.map((r) => this.renderRow(r, cols))}</tbody></table></div>${this.renderReadings(vm.rows)}</div>`;
	}

	private renderRow(r: Row, cols: ViewModel['cols']) {
		const glyph = planetGlyph(r.body ?? r.label);
		return html`<tr class="${r.isAngle ? 'angle' : ''}"><td class="body-cell">${glyph ? html`<span class="glyph">${glyph}</span>` : nothing}${r.label}</td><td>${this.signCell(r.sign, r.degree, r.signLabel)}</td>${
				cols.natal
					? html`<td>${r.natalLongitude != null ? this.signFromLongitude(r.natalLongitude) : html`—`}</td>`
					: nothing
			} ${
				cols.house
					? html`<td class="num">${r.house != null ? r.house : html`—`}</td>`
					: nothing
			} ${
				cols.motion
					? html`<td>${
							r.speed != null
								? html`${formatNumber(this.effectiveLang(), r.speed, 3)}${this.t('°/day')}${r.isRetrograde ? html`<span class="retro">℞</span>` : nothing}`
								: html`—`
						}</td>`
					: nothing
			} ${cols.formula ? html`<td class="formula">${r.formula ?? html`—`}</td>` : nothing}</tr>`;
	}

	private signCell(sign: string, degree: number, label = sign) {
		const g = signGlyph(sign);
		return html`<span class="sign">${g ? html`<span class="sg">${g}</span>` : nothing}${formatDegreeInSign(degree)} ${label}</span>`;
	}

	private signFromLongitude(longitude: number) {
		const p = longitudeToSignPosition(longitude);
		return this.signCell(p.sign, p.degree + p.minute / 60);
	}

	private renderReadings(rows: Row[]) {
		const sections: InterpSection[] = rows
			.filter((r) => r.interpretation)
			.map((r) => {
				const glyph = planetGlyph(r.body ?? r.label) ?? '';
				return {
					label: `${glyph} ${r.label}`.trim(),
					aside:
						`${r.signLabel ?? r.sign} ${formatDegreeInSign(r.degree)}`.trim(),
					body: r.interpretation ?? '',
				};
			});
		return this.renderInterpretation(
			sections,
			'positions-readings',
			'Readings',
		);
	}

	private toViewModel(data: PositionsResponse): ViewModel {
		if ('asteroids' in data) {
			return {
				title: this.t('Asteroids'),
				badges: data.houseSystem
					? [{ label: this.t('Houses'), value: data.houseSystem }]
					: [],
				summary: data.summary,
				cols: { house: true, motion: true, formula: false, natal: false },
				rows: data.asteroids.map((a) => ({
					body: a.name,
					label: display(a, 'name'),
					sign: a.sign,
					signLabel: display(a, 'sign'),
					degree: a.degree,
					house: a.house,
					speed: a.speed,
					isRetrograde: a.isRetrograde,
					interpretation: a.interpretation,
				})),
			};
		}
		if ('lilith' in data) {
			return {
				title: this.t('Black Moon Lilith'),
				badges: data.houseSystem
					? [{ label: this.t('Houses'), value: data.houseSystem }]
					: [],
				summary: data.summary,
				cols: { house: true, motion: true, formula: false, natal: false },
				rows: data.lilith.map((l) => ({
					label: this.t('{{variant}} apogee', {
						variant: display(l, 'variant', capitalize(l.variant)),
					}),
					sign: l.sign,
					signLabel: display(l, 'sign'),
					degree: l.degree,
					house: l.house,
					speed: l.speed,
					isRetrograde: l.isRetrograde,
					interpretation: l.interpretation,
				})),
			};
		}
		if ('directed' in data) {
			return {
				title: this.t('Solar arc directions'),
				badges: [
					{
						label: this.t('Arc'),
						value: `${formatNumber(this.effectiveLang(), data.solarArc, 2)}°`,
					},
					{
						label: this.t('Directed to'),
						value: formatDate(this.effectiveLang(), data.targetDate),
					},
				],
				summary: data.summary,
				cols: { house: false, motion: false, formula: false, natal: true },
				rows: data.directed.map((d) => ({
					body: d.name,
					label: display(d, 'name'),
					sign: d.sign,
					signLabel: display(d, 'sign'),
					degree: d.degree,
					natalLongitude: d.natalLongitude,
					interpretation: d.interpretation,
				})),
			};
		}
		if ('lots' in data) {
			return {
				title: this.t('Arabic lots'),
				badges: data.sect
					? [{ label: this.t('Sect'), value: capitalize(data.sect) }]
					: [],
				summary: data.summary,
				cols: { house: false, motion: false, formula: true, natal: false },
				rows: data.lots.map((l) => ({
					body: l.name,
					label: display(l, 'name'),
					sign: l.sign,
					signLabel: display(l, 'sign'),
					degree: l.degree,
					formula: l.formula,
					interpretation: l.interpretation,
				})),
			};
		}
		// Secondary progressions: planets plus the progressed angles.
		const angleRows: Row[] = [];
		if (data.ascendant) {
			angleRows.push({
				label: this.t('Ascendant'),
				sign: data.ascendant.sign,
				signLabel: display(data.ascendant, 'sign'),
				degree: data.ascendant.degree,
				isAngle: true,
			});
		}
		if (data.midheaven) {
			angleRows.push({
				label: this.t('Midheaven'),
				sign: data.midheaven.sign,
				signLabel: display(data.midheaven, 'sign'),
				degree: data.midheaven.degree,
				isAngle: true,
			});
		}
		return {
			title: this.t('Secondary progressions'),
			badges: [
				{
					label: this.t('Progressed to'),
					value: formatDate(this.effectiveLang(), data.targetDate),
				},
				{
					label: this.t('Elapsed'),
					value: this.t('{{years}} yrs', {
						years: formatNumber(this.effectiveLang(), data.elapsedYears, 1),
					}),
				},
			],
			summary: data.summary,
			cols: { house: true, motion: true, formula: false, natal: false },
			rows: [
				...angleRows,
				...data.planets.map((p) => ({
					body: p.name,
					label: display(p, 'name'),
					sign: p.sign,
					signLabel: display(p, 'sign'),
					degree: p.degree,
					house: p.house,
					speed: p.speed,
					isRetrograde: p.isRetrograde,
					interpretation: p.interpretation,
				})),
			],
		};
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'roxy-positions-table': RoxyPositionsTable;
	}
}
